import { SsbdEmergencyCode } from '@app/enums/ssbd-emergency.enum';
import { ConveyorStatus } from '@app/enums/cuss-device-status.enum';

/**
 * Fields in the unsolicited message that can legitimately carry SSBD status codes.
 * Restricted to SD (statusDevice) and BS (bagStatus) to avoid false positives
 * from dimension fields (HT, WT, LT) which may contain numeric values like "139MM".
 */
const STATUS_FIELDS: ReadonlySet<string> = new Set(['SD', 'BS']);

/**
 * All recognized numeric emergency codes as a lookup set.
 */
const KNOWN_NUMERIC_CODES: ReadonlySet<number> = new Set([
  SsbdEmergencyCode.EMERGENCY_PRESSED,
  SsbdEmergencyCode.EMERGENCY_UNLOCKED,
  SsbdEmergencyCode.NOT_RESPONDING,
  SsbdEmergencyCode.DIAGNOSTIC_RESET,
]);

/**
 * String values from SD/BS fields that map to emergency codes.
 * The physical SSBD device typically sends string-based statuses (EMCY, NORM)
 * in unsolicited messages, while numeric codes (139, 302) appear in
 * statusChange/statusIsOK events.
 *
 * Note: NORM is intentionally NOT mapped here. It is handled separately
 * in the emergency service (only closes modal if one is already open)
 * to avoid every normal message being treated as an emergency event.
 */
const STRING_TO_CODE: ReadonlyMap<string, SsbdEmergencyCode> = new Map([
  [ConveyorStatus.EMERGENCY, SsbdEmergencyCode.EMERGENCY_PRESSED], // EMCY → 139
]);

/**
 * Extracts SSBD emergency status codes from a raw unsolicited message string.
 *
 * The raw format is `#`-delimited key=value pairs, e.g.:
 *   `HIKSON1Q05=1W0C=0#CB=1#SM=B#RB=B#BS=B#CA=B#SD=EMCY`
 *   `#SD=139#BS=NORM#CW=12.5KG#HT=450MM#LT=600MM#WT=350MM`
 *
 * Recognizes both:
 * - String values: EMCY → emergency pressed, NORM → diagnostic reset (clear)
 * - Numeric values: 139 → emergency pressed, 302 → hardware error, 122 → reset
 *
 * Only the SD and BS fields are inspected to avoid false positives from
 * dimension fields (HT/WT/LT) that happen to carry numeric values like 139.
 *
 * @param raw - The unsolicited message string from the SSBD device
 * @returns Array of unique `SsbdEmergencyCode` values found (may be empty)
 */
export function extractSsbdStatusCodes(raw: string | null | undefined): SsbdEmergencyCode[] {
  if (!raw) {
    return [];
  }

  const codes: SsbdEmergencyCode[] = [];
  const seen = new Set<number>();

  const params = raw.split('#');
  for (const param of params) {
    if (!param) continue;

    const eqIndex = param.indexOf('=');
    if (eqIndex === -1) continue;

    const key = param.substring(0, eqIndex);
    if (!STATUS_FIELDS.has(key)) continue;

    const val = param.substring(eqIndex + 1);

    // Try string-based status first (EMCY, NORM, etc.)
    const stringCode = STRING_TO_CODE.get(val);
    if (stringCode !== undefined) {
      if (!seen.has(stringCode)) {
        seen.add(stringCode);
        codes.push(stringCode);
      }
      continue;
    }

    // Try numeric codes (139, 302, 122)
    const numVal = Number(val);
    if (!Number.isNaN(numVal) && KNOWN_NUMERIC_CODES.has(numVal)) {
      if (!seen.has(numVal)) {
        seen.add(numVal);
        codes.push(numVal as SsbdEmergencyCode);
      }
    }
  }

  return codes;
}

/**
 * Extracts ONLY the SD (statusDevice) field value from a raw unsolicited message.
 *
 * Used for modal close decisions to avoid false positives from the BS (bagStatus) field.
 * For example, `#SD=302#BS=NORM` should NOT close the modal (SD is an error code),
 * even though BS carries the recovery value `NORM`.
 *
 * @param raw - The unsolicited message string from the SSBD device
 * @returns The SD field value as a string, or null if not found
 */
export function extractDeviceStatusValue(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  const params = raw.split('#');
  for (const param of params) {
    if (!param) continue;
    const eqIndex = param.indexOf('=');
    if (eqIndex === -1) continue;
    const key = param.substring(0, eqIndex);
    if (key === 'SD') {
      return param.substring(eqIndex + 1) || null;
    }
  }

  return null;
}
